import classNames from 'classnames';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { StateMessage } from '@/components/ui/controls';
import { Card } from '@/components/ui/page';
import { btn, field } from '@/components/ui/styles';
import QrCode from '@/features/assistant/qr-code';
import { errorOf, useAssistant } from '@/features/assistant/use-assistant';
import type { EventLeadResult } from '@/types/api';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';

/** The event's QR code: scanning it opens a private chat with Assistant, the message written. */
const EventQR = ({ event }: { event: string }) => {
  const { data, error } = useApi<{ link: string }>(event ? '/assistant/event-link' : null, { event });
  return (
    <Card title="Or let them scan">
      {error && !data ? (
        <p className="text-sm text-danger">The QR code could not be made. Is Assistant's account connected, with a username?</p>
      ) : !data?.link ? (
        <div className="mx-auto aspect-square w-full max-w-[280px] animate-pulse rounded-lg bg-layer" />
      ) : (
        <div className="flex flex-col items-center gap-3">
          <QrCode link={data.link} label={`QR code: a chat with Assistant about ${event}`} className="w-full max-w-[280px]" />
          <p className="text-center text-sm text-fg-muted">
            They scan it with their phone, Telegram opens a chat with Assistant with the message written, and they send it. They become a lead from {event}.
          </p>
        </div>
      )}
    </Card>
  );
};

/** What became of the visitor: Assistant's message, or the chat link by email. */
const Outcome = ({ result }: { result: EventLeadResult }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
    } catch {
      /* the link stays on screen to copy by hand */
    }
  };
  const title =
    result.telegram === 'sent'
      ? 'Added, and Assistant wrote to them'
      : result.email === 'sent'
        ? 'Added, and the chat link went by email'
        : result.email === 'simulated'
          ? 'Added. The email is not sent yet'
          : 'Added as a lead';
  return (
    <Card title={title}>
      <div className="space-y-3 text-sm text-fg">
        {result.telegram === 'sent' && <p>They should see Assistant’s message on their phone now.</p>}
        {result.email === 'sent' && <p>The email has a link to a chat with Assistant. When they open it, the chat is theirs in Prisma.</p>}
        {result.email === 'simulated' && <p>Nothing goes out by email yet (no email provider). Send them this chat link yourself; when they open it, the chat is theirs in Prisma.</p>}
        {result.link && (
          <div className="flex flex-col gap-2 rounded-lg bg-layer p-3 sm:flex-row sm:items-center">
            <code className="min-w-0 flex-1 break-all text-xs text-fg-muted">{result.link}</code>
            <button type="button" className={btn.secondary} onClick={copy}>
              {copied ? 'Copied' : 'Copy the link'}
            </button>
          </div>
        )}
        {result.problem && <p className="text-fg-muted">{result.problem}</p>}
        <Link to={`/leads/${encodeURIComponent(result.lead.id)}`} className={btn.link}>
          Open the lead
        </Link>
      </div>
    </Card>
  );
};

/**
 * The hostess's screen (ASSIST-1): add a visitor met at the event, with any of a phone
 * number, a Telegram username and an email address. Assistant writes to them on Telegram at
 * once; one with only an email gets the chat link by email. A visitor Telegram cannot find
 * scans the event's QR code instead. Made for a phone or a tablet at the stand.
 */
const HostessPage = () => {
  const status = useAssistant();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [result, setResult] = useState<EventLeadResult | null>(null);

  if (status.data && !status.data.enabled) {
    return <StateMessage title="Not available here">Assistant is not turned on in this environment.</StateMessage>;
  }

  const event = status.data?.event ?? '';
  const onTelegram = Boolean(phone.trim() || username.trim());
  const any = onTelegram || Boolean(email.trim());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      const res = await http.post<EventLeadResult>('/assistant/event-leads', { name, phone, username, email, consent });
      setResult(res.data);
      setName('');
      setPhone('');
      setUsername('');
      setEmail('');
      setConsent(false);
    } catch (err) {
      setProblem(errorOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl pt-2">
      {status.data && !status.data.connected && (
        <p role="note" className="mb-4 rounded-card bg-layer px-4 py-3 text-sm text-fg-muted">
          Assistant's Telegram account is not connected, so it cannot write to anyone yet. Visitors are still added as leads. An admin connects it in Settings, Assistant.
        </p>
      )}
      <div className="space-y-4">
        <Card>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <p className={field.label}>Event</p>
              <p className="text-base text-fg-strong">{event || '…'}</p>
            </div>
            <div>
              <label htmlFor="hostess-name" className={field.label}>
                Visitor's name
              </label>
              <input id="hostess-name" className={field.input} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
            </div>
            <p className="text-sm text-fg-muted">Fill in at least one of these.</p>
            <div>
              <label htmlFor="hostess-phone" className={field.label}>
                Phone number, with the country code
              </label>
              <input id="hostess-phone" className={field.input} value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="off" placeholder="+40 7…" />
            </div>
            <div>
              <label htmlFor="hostess-username" className={field.label}>
                Telegram username
              </label>
              <input id="hostess-username" className={field.input} value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" autoCapitalize="none" spellCheck={false} placeholder="@username" />
            </div>
            <div>
              <label htmlFor="hostess-email" className={field.label}>
                Email address
              </label>
              <input id="hostess-email" type="email" className={field.input} value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" autoComplete="off" autoCapitalize="none" spellCheck={false} />
            </div>
            <label className="flex min-h-11 items-center gap-3 text-sm text-fg">
              <input type="checkbox" className={field.checkbox} checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              They agreed that we write to them
            </label>
            {problem && <p className="text-sm text-danger">{problem}</p>}
            <button type="submit" className={classNames(btn.primary, 'w-full md:w-auto')} disabled={busy || !consent || !any}>
              {busy ? 'Adding…' : onTelegram || !any ? 'Add and write on Telegram' : 'Add and send email'}
            </button>
          </form>
        </Card>

        {result && <Outcome result={result} />}

        {event && <EventQR event={event} />}
      </div>
    </div>
  );
};

export default HostessPage;
